import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Zap, FileText, Star, Link2, TrendingUp, LayoutGrid,
  Briefcase, Copy, Check, ChevronDown, ChevronUp, AlertCircle,
  Sparkles, Trophy, ArrowRight, RotateCcw,
  BookOpen, Download, ZoomIn, ZoomOut, X, Maximize2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Layout, ScoreBadge } from "@/components/Layout";
import { useLanguage } from "@/lib/LanguageContext";
import { staggerContainer, staggerItem, springPresets } from "@/lib/motion";
import { ROUTE_PATHS } from "@/lib/index";
import type { ProfileInput, ProfileAnalysis, AnalysisSection, SectionAnalysis, Language } from "@/lib/index";
import {
  analyzeHeadline, analyzeAbout, analyzeSkills,
  analyzeCoherence, analyzeEngagement, analyzePillars, analyzeServices,
  analyzeExperience,
} from "@/api/analysis";
import { useProfile } from "@/lib/ProfileContext";
import { copyToClipboard } from "@/lib/clipboard";
const ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  headline: Zap,
  about: FileText,
  skills: Star,
  experience: Briefcase,
  services: LayoutGrid,
  coherence: Link2,
  engagement: TrendingUp,
  pillars: LayoutGrid,
};

const SECTION_ORDER: AnalysisSection[] = [
  "headline", "about", "skills", "experience", "services", "coherence", "engagement", "pillars",
];

export default function HomePage() {
  const { t, language } = useLanguage();
  const { profile, setProfile, resetProfile } = useProfile();
  const [input, setInput] = useState<ProfileInput>({
    ...profile,
    experiences: profile.experiences || [],
    servicesBio: profile.servicesBio || "",
    servicesList: profile.servicesList || "",
  });

  const addExperience = () => {
    setInput(prev => ({
      ...prev,
      experiences: [...prev.experiences, {
        id: Math.random().toString(36).substr(2, 9),
        title: "",
        type: "Full-time",
        company: "",
        description: "",
        skills: ""
      }]
    }));
  };

  const removeExperience = (id: string) => {
    setInput(prev => ({
      ...prev,
      experiences: prev.experiences.filter(exp => exp.id !== id)
    }));
  };

  const handleExperienceChange = (id: string, field: string, value: string) => {
    setInput(prev => ({
      ...prev,
      experiences: prev.experiences.map(exp => exp.id === id ? { ...exp, [field]: value } : exp)
    }));
  };

  // Update local input if profile context changes (e.g. on reset)
  useEffect(() => {
    setInput(profile);
  }, [profile]);
  const [analysis, setAnalysis] = useState<ProfileAnalysis | null>(null);
  const [analyzingSection, setAnalyzingSection] = useState<AnalysisSection | null>(null);
  const [completedSections, setCompletedSections] = useState<Set<AnalysisSection>>(new Set());
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [expandedSection, setExpandedSection] = useState<AnalysisSection | null>("headline");
  const [copiedSection, setCopiedSection] = useState<AnalysisSection | null>(null);
  const resultsRef = useRef<HTMLDivElement>(null);
  const [isManualOpen, setIsManualOpen] = useState(false);
  const [zoom, setZoom] = useState(100);

  const handleChange = (field: keyof ProfileInput) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => setInput((prev) => ({ ...prev, [field]: e.target.value }));

  const runAnalysis = async () => {
    if (!input.headline.trim() && !input.about.trim()) {
      toast.error(t("home.errorInput"));
      return;
    }
    setIsAnalyzing(true);
    setAnalysis(null);
    setCompletedSections(new Set());
    setProfile(input); // Sync with context for Creative Studio pre-fill

    const results: Partial<ProfileAnalysis> = {};
    const analyzers: Record<AnalysisSection, (i: ProfileInput, l: Language) => SectionAnalysis> = {
      headline: analyzeHeadline,
      about: analyzeAbout,
      skills: analyzeSkills,
      experience: analyzeExperience,
      services: analyzeServices,
      coherence: analyzeCoherence,
      engagement: analyzeEngagement,
      pillars: analyzePillars,
    };

    for (const section of SECTION_ORDER) {
      setAnalyzingSection(section);
      await new Promise((res) => setTimeout(res, 600));
      results[section] = analyzers[section](input, language);
      setCompletedSections((prev) => new Set([...prev, section]));
    }

    setAnalyzingSection(null);

    const scores = SECTION_ORDER.map((s) => (results[s] as SectionAnalysis).score);
    const overallScore = Math.round(scores.reduce((a, b) => a + b, 0) / scores.length);

    const fullAnalysis: ProfileAnalysis = {
      headline: results.headline!,
      about: results.about!,
      skills: results.skills!,
      experience: results.experience!,
      services: results.services!,
      coherence: results.coherence!,
      engagement: results.engagement!,
      pillars: results.pillars!,
      overallScore,
    };

    setAnalysis(fullAnalysis);
    setIsAnalyzing(false);
    setExpandedSection("headline");

    setTimeout(() => {
      resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 300);

    toast.success(`${t("home.successAnalysis")}${overallScore}%`);
  };

  const handleCopy = async (section: AnalysisSection, text: string) => {
    if (!text) return;
    const success = await copyToClipboard(text, t("home.copied"));
    if (success) {
      setCopiedSection(section);
      setTimeout(() => setCopiedSection(null), 2000);
    }
  };

  const reset = () => {
    setAnalysis(null);
    setCompletedSections(new Set());
    resetProfile();
    setExpandedSection("headline");
  };

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4 py-8 space-y-10">
        {/* ── Hero ── */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={springPresets.gentle}
          className="text-center space-y-4"
        >
          <div className="flex items-center justify-center mb-4">
            <div className="w-20 h-20 rounded-full overflow-hidden ring-4 ring-primary/30">
              <img src="/logo.png" alt="360Brew Optimizer" className="w-full h-full object-cover" />
            </div>
          </div>
          <h1 className="text-3xl sm:text-4xl font-extrabold text-foreground leading-tight">
            {t("home.title")}{" "}
            <span className="text-transparent bg-clip-text"
              style={{ backgroundImage: "linear-gradient(135deg, oklch(0.70 0.20 175), oklch(0.78 0.18 48))" }}>
              {t("home.subtitle")}
            </span>
          </h1>
          <p className="text-muted-foreground text-base sm:text-lg max-w-2xl mx-auto leading-relaxed">
            {t("home.description")}
          </p>
        </motion.div>

        {/* ── Manual Trigger ── */}
        <div className="flex justify-center">
          <Dialog open={isManualOpen} onOpenChange={setIsManualOpen}>
            <DialogTrigger asChild>
              <Button 
                variant="outline" 
                className="rounded-full px-6 py-6 border-primary/40 hover:border-primary bg-primary/5 hover:bg-primary/10 group transition-all duration-300"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-primary group-hover:scale-110 transition-transform">
                    <img src="/logo.png" alt="Logo" className="w-full h-full object-cover" />
                  </div>
                  <div className="text-left">
                    <span className="block text-xs font-bold uppercase tracking-widest text-primary">{t("common.openManual")}</span>
                    <span className="block text-[10px] text-muted-foreground uppercase">360Brew Blue XL Guide</span>
                  </div>
                  <BookOpen className="w-5 h-5 ml-2 text-primary group-hover:translate-x-1 transition-transform" />
                </div>
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-5xl w-[95vw] h-[90vh] p-0 gap-0 bg-card border-primary/20 flex flex-col overflow-hidden">
              <DialogHeader className="p-4 border-b border-border/40 flex flex-row items-center justify-between shrink-0">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full overflow-hidden border border-primary">
                    <img src="/logo.png" alt="Logo" className="w-full h-full object-cover" />
                  </div>
                  <DialogTitle className="text-lg font-bold uppercase tracking-wider">{t("common.openManual")}</DialogTitle>
                </div>
                <div className="flex items-center gap-2 pr-8">
                  <Button variant="ghost" size="icon" onClick={() => setZoom(z => Math.max(50, z - 10))} title={t("common.zoomOut")}>
                    <ZoomOut className="w-4 h-4" />
                  </Button>
                  <span className="text-xs font-mono w-12 text-center">{zoom}%</span>
                  <Button variant="ghost" size="icon" onClick={() => setZoom(z => Math.min(200, z + 10))} title={t("common.zoomIn")}>
                    <ZoomIn className="w-4 h-4" />
                  </Button>
                  <div className="w-px h-4 bg-border/40 mx-2" />
                  <Button variant="secondary" size="sm" asChild className="h-8 rounded-lg font-bold">
                    <a href="/manual.pdf" download="360Brew_Manual.pdf">
                      <Download className="w-4 h-4 mr-2" />
                      {t("common.downloadManual")}
                    </a>
                  </Button>
                </div>
              </DialogHeader>
              <ScrollArea className="flex-1 bg-background/50">
                <div className="p-8 flex justify-center origin-top transition-transform duration-200" style={{ transform: `scale(${zoom / 100})` }}>
                  <div className="w-full max-w-4xl bg-white text-black rounded-lg shadow-2xl overflow-hidden">
                    <iframe 
                      src="/manual.html" 
                      className="w-full h-[1500px] border-none"
                      title="Manual Content"
                    />
                  </div>
                </div>
              </ScrollArea>
            </DialogContent>
          </Dialog>
        </div>

        {/* ── Input Form ── */}
        {!isAnalyzing && !analysis && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...springPresets.gentle, delay: 0.1 }}
            className="rounded-2xl border border-border/60 bg-card p-6 sm:p-8 space-y-6"
            style={{ boxShadow: "0 8px 40px oklch(0.10 0.03 235 / 0.6)" }}
          >
            <div className="flex items-center gap-3 mb-2">
              <Sparkles className="w-5 h-5 text-primary" />
              <h2 className="text-lg font-bold text-foreground">{t("home.inputTitle")}</h2>
            </div>

            <div className="grid sm:grid-cols-2 gap-5">
              <div className="space-y-2">
                <Label htmlFor="fullName" className="text-sm font-medium text-foreground">
                  {t("home.fullNameLabel")}
                </Label>
                <Input
                  id="fullName"
                  value={input.fullName}
                  onChange={handleChange("fullName")}
                  placeholder={t("home.placeholderFullName")}
                  className="bg-input/50 border-border/60 focus:border-primary"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="profileUrl" className="text-sm font-medium text-foreground">
                  {t("home.profileUrlLabel")}
                </Label>
                <Input
                  id="profileUrl"
                  value={input.profileUrl}
                  onChange={handleChange("profileUrl")}
                  placeholder={t("home.placeholderProfileUrl")}
                  className="bg-input/50 border-border/60 focus:border-primary"
                />
              </div>

              <div className="sm:col-span-2 space-y-2">
                <Label htmlFor="headline" className="text-sm font-medium text-foreground">
                  {t("home.headlineLabel")} <span className="text-primary">*</span>
                </Label>
                <Input
                  id="headline"
                  value={input.headline}
                  onChange={handleChange("headline")}
                  placeholder={t("home.placeholderHeadline")}
                  className="bg-input/50 border-border/60 focus:border-primary"
                />
                <p className="text-xs text-muted-foreground">{input.headline.length}/220 characters</p>
              </div>

              <div className="sm:col-span-2 space-y-2">
                <Label htmlFor="about" className="text-sm font-medium text-foreground">
                  {t("home.aboutLabel")} <span className="text-primary">*</span>
                </Label>
                <Textarea
                  id="about"
                  value={input.about}
                  onChange={handleChange("about")}
                  placeholder={t("home.placeholderAbout")}
                  className="bg-input/50 border-border/60 focus:border-primary min-h-[120px] resize-y"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="skills" className="text-sm font-medium text-foreground">{t("home.skillsLabel")}</Label>
                <Input
                  id="skills"
                  value={input.skills}
                  onChange={handleChange("skills")}
                  placeholder={t("home.placeholderSkills")}
                  className="bg-input/50 border-border/60"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="industry" className="text-sm font-medium text-foreground">{t("home.industryLabel")}</Label>
                <Input
                  id="industry"
                  value={input.industry}
                  onChange={handleChange("industry")}
                  placeholder={t("home.placeholderIndustry")}
                  className="bg-input/50 border-border/60"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="position" className="text-sm font-medium text-foreground">{t("home.positionLabel")}</Label>
                <Input
                  id="position"
                  value={input.position}
                  onChange={handleChange("position")}
                  placeholder={t("home.placeholderPosition")}
                  className="bg-input/50 border-border/60"
                />
              </div>

              {/* Experience Section - Blue XL Optimized Dynamic List */}
              <div className="sm:col-span-2 p-6 rounded-3xl border-2 border-primary/20 bg-primary/5 space-y-8">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-primary">
                    <Briefcase className="w-6 h-6" />
                    <h3 className="font-bold uppercase tracking-wider text-sm">Experience (360Brew Blue XL Optimized)</h3>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addExperience}
                    className="rounded-full bg-primary/10 border-primary/30 hover:bg-primary/20 text-primary font-bold"
                  >
                    <Zap className="w-4 h-4 mr-2" />
                    Add Experience
                  </Button>
                </div>

                <div className="space-y-8">
                  {input.experiences.map((exp, index) => (
                    <motion.div
                      key={exp.id}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      className="p-5 rounded-2xl bg-background border border-border/40 space-y-4 relative group"
                    >
                      <button
                        onClick={() => removeExperience(exp.id)}
                        className="absolute top-4 right-4 p-1 rounded-full bg-muted hover:bg-red-500/10 hover:text-red-500 text-muted-foreground transition-all opacity-0 group-hover:opacity-100"
                        title="Remove"
                      >
                        <X className="w-4 h-4" />
                      </button>
                      
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center font-bold text-primary text-xs">{index + 1}</div>
                        <h4 className="font-bold text-sm text-foreground uppercase tracking-tight">Job #{index + 1}</h4>
                      </div>

                      <div className="grid sm:grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                          <Label className="text-xs font-bold uppercase text-muted-foreground">{t("home.experienceTitleLabel")}</Label>
                          <Input
                            value={exp.title}
                            onChange={(e) => handleExperienceChange(exp.id, "title", e.target.value)}
                            placeholder={t("home.placeholderExperienceTitle")}
                            className="bg-muted/30 border-none focus:ring-1 focus:ring-primary h-10 text-sm"
                          />
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-xs font-bold uppercase text-muted-foreground">{t("home.experienceTypeLabel")}</Label>
                          <Input
                            value={exp.type}
                            onChange={(e) => handleExperienceChange(exp.id, "type", e.target.value)}
                            placeholder={t("home.placeholderExperienceType")}
                            className="bg-muted/30 border-none focus:ring-1 focus:ring-primary h-10 text-sm"
                          />
                        </div>
                      </div>

                      <div className="space-y-1.5">
                        <Label className="text-xs font-bold uppercase text-muted-foreground">{t("home.experienceCompanyLabel")}</Label>
                        <Input
                          value={exp.company}
                          onChange={(e) => handleExperienceChange(exp.id, "company", e.target.value)}
                          placeholder={t("home.placeholderExperienceCompany")}
                          className="bg-muted/30 border-none focus:ring-1 focus:ring-primary h-10 text-sm"
                        />
                      </div>

                      <div className="space-y-1.5">
                        <Label className="text-xs font-bold uppercase text-muted-foreground">{t("home.experienceDescLabel")}</Label>
                        <Textarea
                          value={exp.description}
                          onChange={(e) => handleExperienceChange(exp.id, "description", e.target.value)}
                          placeholder={t("home.placeholderExperienceDesc")}
                          className="bg-muted/30 border-none focus:ring-1 focus:ring-primary min-h-[120px] text-sm resize-none"
                        />
                      </div>

                      <div className="space-y-1.5">
                        <Label className="text-xs font-bold uppercase text-muted-foreground">{t("home.experienceSkillsLabel")}</Label>
                        <Input
                          value={exp.skills}
                          onChange={(e) => handleExperienceChange(exp.id, "skills", e.target.value)}
                          placeholder={t("home.placeholderExperienceSkills")}
                          className="bg-muted/30 border-none focus:ring-1 focus:ring-primary h-10 text-sm"
                        />
                      </div>
                    </motion.div>
                  ))}

                  {input.experiences.length === 0 && (
                    <div className="text-center py-8 border-2 border-dashed border-border/40 rounded-2xl bg-background/50">
                      <p className="text-sm text-muted-foreground mb-4">No professional experiences added yet.</p>
                      <Button variant="outline" size="sm" onClick={addExperience} className="rounded-full border-primary/40 text-primary">
                        <Zap className="w-4 h-4 mr-2" />
                        Add First Experience
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              {/* Services Section - Banded Style */}
              <div className="sm:col-span-2 p-6 rounded-2xl border-2 border-primary/20 bg-primary/5 space-y-5">
                <div className="flex items-center gap-2 text-primary">
                  <LayoutGrid className="w-5 h-5" />
                  <h3 className="font-bold uppercase tracking-wider text-sm">LinkedIn Services Section (Banded)</h3>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="servicesBio" className="text-sm font-medium text-foreground">
                    {t("home.servicesBioLabel")}
                  </Label>
                  <Textarea
                    id="servicesBio"
                    value={input.servicesBio}
                    onChange={handleChange("servicesBio")}
                    placeholder={t("home.placeholderServicesBio")}
                    maxLength={500}
                    className="bg-background border-border/60 focus:border-primary min-h-[80px] resize-none"
                  />
                  <div className="flex justify-between items-center">
                    <p className="text-[10px] text-muted-foreground uppercase tracking-tighter">360Brew Semantic Bio Optimization</p>
                    <p className="text-xs text-muted-foreground">{input.servicesBio?.length || 0}/500</p>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="servicesList" className="text-sm font-medium text-foreground">
                    {t("home.servicesListLabel")}
                  </Label>
                  <Input
                    id="servicesList"
                    value={input.servicesList}
                    onChange={handleChange("servicesList")}
                    placeholder={t("home.placeholderServicesList")}
                    className="bg-background border-border/60 focus:border-primary"
                  />
                </div>
              </div>
            </div>

            <Button
              onClick={runAnalysis}
              className="w-full h-12 text-base font-bold bg-primary hover:bg-primary/90 transition-all duration-300 rounded-xl"
              style={{ boxShadow: "0 4px 20px oklch(0.70 0.20 175 / 0.4)" }}
            >
              <Zap className="w-5 h-5 mr-2" />
              {t("home.analyzeBtn")}
            </Button>
          </motion.div>
        )}

        {/* ── Progress Animation ── */}
        {isAnalyzing && (
          <div className="space-y-8 py-12">
            <div className="flex flex-col items-center justify-center space-y-4">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                className="w-16 h-16 rounded-full border-4 border-primary/20 border-t-primary"
              />
              <p className="text-xl font-bold text-foreground animate-pulse">{t("home.analyzing")}</p>
            </div>
            
            <div className="max-w-md mx-auto space-y-3">
              {SECTION_ORDER.map((section) => {
                const isCompleted = completedSections.has(section);
                const isCurrent = analyzingSection === section;
                return (
                  <div key={section} className="flex items-center justify-between p-3 rounded-lg bg-card/50 border border-border/40">
                    <div className="flex items-center gap-3">
                      {isCompleted ? <Check className="w-4 h-4 text-emerald-400" /> : <div className="w-4 h-4 rounded-full border-2 border-muted" />}
                      <span className={`text-sm font-medium ${isCurrent ? 'text-primary' : 'text-muted-foreground'}`}>
                        {t(`sections.${section}.label`)}
                      </span>
                    </div>
                    {isCurrent && <motion.div layoutId="arrow" className="w-1.5 h-1.5 rounded-full bg-primary" />}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── Analysis Results ── */}
        {analysis && !isAnalyzing && (
          <div ref={resultsRef} className="space-y-8">
            {/* Summary Score Card */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
          className="rounded-3xl border border-primary/40 bg-primary/5 p-8 text-center space-y-4"
          style={{ boxShadow: "0 20px 60px oklch(0.10 0.03 235 / 0.8)" }}
        >
          {input.fullName && (
            <div className="mb-4">
              <h3 className="text-2xl font-black text-foreground">{t("common.analysisFor")} {input.fullName}</h3>
              {input.profileUrl && (
                <a href={input.profileUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm text-primary font-bold hover:underline mt-1">
                  <Link2 className="w-4 h-4" />
                  {t("common.viewProfile")}
                </a>
              )}
            </div>
          )}
          <p className="text-sm font-bold text-primary uppercase tracking-widest">{t("home.overallScore")}</p>
          <div className="relative inline-block">
                <ScoreBadge score={analysis.overallScore} size="lg" />
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: "100%" }}
                  transition={{ duration: 1.5, ease: "easeOut" }}
                  className="h-1.5 bg-primary/30 rounded-full mt-2"
                />
              </div>
              <div className="flex justify-center gap-4 pt-4">
                <Button variant="outline" onClick={reset} className="rounded-xl">
                  <RotateCcw className="w-4 h-4 mr-2" />
                  {t("home.resetBtn")}
                </Button>
              </div>
            </motion.div>

            {/* Accordion Sections */}
            <div className="space-y-4">
              {SECTION_ORDER.map((section) => {
                const data = analysis[section] as SectionAnalysis;
                const isExpanded = expandedSection === section;
                const Icon = ICON_MAP[section];
                
                return (
                  <motion.div
                    key={section}
                    className={`rounded-2xl border transition-all duration-300 ${isExpanded ? 'border-primary/50 bg-card' : 'border-border/60 bg-card/40'}`}
                  >
                    <button
                      onClick={() => setExpandedSection(isExpanded ? null : section)}
                      className="w-full flex items-center justify-between p-5 text-left"
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isExpanded ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>
                          <Icon className="w-5 h-5" />
                        </div>
                        <div>
                          <h3 className="font-bold text-foreground">{t(`sections.${section}.label`)}</h3>
                          <p className="text-xs text-muted-foreground">{t(`sections.${section}.description`)}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <ScoreBadge score={data.score} size="md" />
                        {isExpanded ? <ChevronUp className="w-5 h-5 text-muted-foreground" /> : <ChevronDown className="w-5 h-5 text-muted-foreground" />}
                      </div>
                    </button>

                    <AnimatePresence>
                      {isExpanded && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="overflow-hidden"
                        >
                          <div className="px-5 pb-6 space-y-6 border-t border-border/40 pt-6">
                            {/* Analysis */}
                            <div className="space-y-2">
                              <h4 className="text-xs font-bold text-primary uppercase tracking-wider flex items-center gap-2">
                                <AlertCircle className="w-3.5 h-3.5" />
                                {t("home.analysisTitle")}
                              </h4>
                              <p className="text-sm text-muted-foreground leading-relaxed">{data.analysis}</p>
                            </div>

                            {/* Suggestions */}
                            <div className="space-y-3">
                              <h4 className="text-xs font-bold text-accent uppercase tracking-wider flex items-center gap-2">
                                <Trophy className="w-3.5 h-3.5" />
                                {t("home.suggestionsTitle")}
                              </h4>
                              <div className="grid sm:grid-cols-2 gap-2">
                                {(data.suggestions || []).map((s, i) => (
                                  <div key={i} className="flex items-start gap-2 p-2.5 rounded-lg bg-accent/5 border border-accent/10">
                                    <span className="text-accent font-bold text-xs mt-0.5">{i+1}.</span>
                                    <span className="text-xs text-muted-foreground">{s}</span>
                                  </div>
                                ))}
                              </div>
                            </div>

                            {/* Optimized Text */}
                            <div className="space-y-3">
                              <div className="flex items-center justify-between">
                                <h4 className="text-xs font-bold text-emerald-400 uppercase tracking-wider flex items-center gap-2">
                                  <Sparkles className="w-3.5 h-3.5" />
                                  {t("home.optimizedTitle")}
                                </h4>
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="secondary"
                                  className="h-8 rounded-lg text-xs font-bold shadow-sm hover:bg-secondary/80 active:scale-95 transition-transform"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleCopy(section, data.optimizedText);
                                  }}
                                >
                                  {copiedSection === section ? <Check className="w-3.5 h-3.5 mr-1" /> : <Copy className="w-3.5 h-3.5 mr-1" />}
                                  {copiedSection === section ? t("home.copied") : t("home.copyBtn")}
                                </Button>
                              </div>
                              <div className="relative group">
                                <pre className="p-4 rounded-xl bg-muted/50 border border-border/40 text-xs text-muted-foreground whitespace-pre-wrap font-sans leading-relaxed">
                                  {data.optimizedText}
                                </pre>
                              </div>
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                );
              })}
            </div>
            
            {/* CTA Final */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="p-6 rounded-2xl border border-primary/30 bg-primary/5 flex flex-col sm:flex-row items-center justify-between gap-4"
            >
              <div className="text-center sm:text-left">
                <p className="font-bold text-foreground">{t("home.ctaTitle")}</p>
                <p className="text-sm text-muted-foreground">{t("home.ctaDesc")}</p>
              </div>
              <Button asChild className="rounded-xl">
                <a href="https://www.linkedin.com/in/giovanni-burdi" target="_blank" rel="noopener noreferrer">
                  {t("home.ctaBtn")}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </a>
              </Button>
            </motion.div>

            {/* Creative Studio Deep Link */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="p-6 rounded-2xl border border-accent/30 bg-accent/5 flex flex-col sm:flex-row items-center justify-between gap-4"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center shadow-lg shadow-accent/10">
                  <Sparkles className="w-6 h-6 text-accent" />
                </div>
                <div className="text-center sm:text-left">
                  <p className="font-bold text-foreground">{t("nav.studio")}</p>
                  <p className="text-xs text-muted-foreground">Transform your 360Brew data into cinematic visuals and assets.</p>
                </div>
              </div>
              <Button asChild variant="secondary" className="rounded-xl group h-12 px-6 border border-accent/20 hover:bg-accent/10">
                <Link to={ROUTE_PATHS.STUDIO}>
                  Go to Creative Studio
                  <Sparkles className="w-4 h-4 ml-2 group-hover:animate-pulse" />
                </Link>
              </Button>
            </motion.div>
          </div>
        )}
      </div>
    </Layout>
  );
}
